/**
 * FeedRanker — ротация ленты главной.
 *
 * Задача: каждый юзер видит РАЗНЫЕ карточки, но порядок стабилен внутри
 * сессии (иначе дозагрузка батчей даст дубли и пропуски).
 *
 * Три механики (см. docs/plan-feed-rotation-2026-07-20.md):
 *   1. Seeded keyed sort — порядок = f(seed, id). Сид свой у юзера и
 *      меняется раз в сутки. Keyed sort (а не Fisher-Yates) выбран
 *      потому, что добавление нового стиля НЕ пересобирает порядок
 *      остальных и логика переносима в SQL при серверной пагинации.
 *   2. Полки fresh/proven/explore со смешиванием ПО ОКНУ — иначе
 *      explore уезжает вниз ленты, где его никто не видит.
 *   3. Anti-repeat: показанное дисконтируется (не блокируется) с
 *      экспоненциальным затуханием. Показ фиксируется по РЕАЛЬНОЙ
 *      видимости карточки, не по факту рендера.
 *
 * Модуль чистый — DOM не трогает. Импрешены фиксирует вызывающий код.
 */
window.FeedRanker = (function () {
    'use strict';

    // ── Конфиг ──────────────────────────────────────────────────────
    // Квоты полок. Прод-данные на 2026-07-20: 83% стилей имеют 0 ремиксов
    // (их просто не видели), свежих (<30д) всего 22 из 353. Поэтому
    // explore щедрый — каталог нужно прогревать.
    const QUOTAS = { proven: 0.45, fresh: 0.25, explore: 0.30 };
    // Окно смешивания: квоты выполняются на каждых WINDOW карточках,
    // так что разнообразие есть уже на первом экране.
    const WINDOW = 6;
    const FRESH_DAYS = 30;          // граница полки fresh

    // Принудительный подмес новинок (юзер 2026-08-25: «подмешивались 3-4
    // новых начиная со 2»). Ранкер сам по себе новинки не гарантирует:
    // полка fresh — это лишь 25% квоты, а её скор конкурирует с proven,
    // так что на первом экране свежего могло не оказаться вовсе (на
    // проде свежих <30д всего 22 из 353). Поэтому N самых новых
    // ставим жёстко, начиная со ВТОРОЙ позиции — первая остаётся за
    // персональной ротацией, она отвечает за попадание в интерес.
    // Порог совпадает с бейджем NEW (references.js): в ленту
    // поднимается ровно то, что помечено как новое, без расхождения.
    const PIN_NEW = { start: 1, count: 4, maxAgeDays: 7 };

    // Видео идут по ленте ступенькой (юзер 2026-09-17). Видео-шаблонов в
    // каталоге единицы — 5 из 420 на 17.09.2026, — и при честной ротации
    // человек их просто не встречает: он не знает, что видео тут есть.
    // Первое ставим на позицию 3, дальше через каждые STEP: между двумя
    // видео остаётся три обычных карточки, то есть примерно полтора ряда
    // сетки 2×N — заметно, но лента не превращается в видео-ленту.
    // Порядок самих видео — тот, что посчитал ранкер: лучшее идёт первым.
    const PIN_VIDEO = { first: 3, step: 4 };

    // Anti-repeat
    const SEEN_KEY = 'lora_feed_seen_v1';
    const SEEN_TTL_MS = 14 * 24 * 3600 * 1000;   // храним 14 дней
    const SEEN_HALF_LIFE_H = 36;                 // штраф падает вдвое за 36ч
    const SEEN_MAX_ENTRIES = 2000;               // LRU-cap (квота localStorage)

    // Сид
    const SEED_KEY = 'lora_feed_seed_v1';
    const SALT_KEY = 'lora_device_salt_v1';
    const FEED_VERSION = 1;   // ручной рычаг: инкремент → переротация у всех

    // ── PRNG: cyrb128 (строка→128 бит) + sfc32 ──────────────────────
    // sfc32 выбран вместо mulberry32: 128-бит состояние, проходит
    // PractRand. mulberry32 пропускает ~треть 32-битных значений.
    function cyrb128(str) {
        let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
        for (let i = 0, k; i < str.length; i++) {
            k = str.charCodeAt(i);
            h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
            h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
            h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
            h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
        }
        h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
        h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
        h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
        h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
        h1 ^= (h2 ^ h3 ^ h4); h2 ^= h1; h3 ^= h1; h4 ^= h1;
        return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
    }

    function sfc32(a, b, c, d) {
        return function () {
            a |= 0; b |= 0; c |= 0; d |= 0;
            const t = (a + b | 0) + d | 0;
            d = d + 1 | 0;
            a = b ^ b >>> 9;
            b = c + (c << 3) | 0;
            c = (c << 21 | c >>> 11);
            c = c + t | 0;
            return (t >>> 0) / 4294967296;   // [0, 1)
        };
    }

    function makeRng(seedStr) {
        const s = cyrb128(String(seedStr));
        return sfc32(s[0], s[1], s[2], s[3]);
    }

    /**
     * Детерминированный ключ элемента: одинаковый seed+id → одинаковое
     * число в [0,1). Основа keyed sort'а.
     */
    function orderKey(seed, id) {
        return cyrb128(seed + '|' + id)[0] / 4294967296;
    }

    // ── Сид сессии ──────────────────────────────────────────────────
    /**
     * Сид фиксируется ОДИН РАЗ за сессию и живёт в sessionStorage.
     * Если пересчитывать на каждый рендер — порядок поедет посреди
     * скролла (юзер, открывший приложение в 23:59, поймает смену суток).
     * Формула: user_id : YYYY-MM-DD : версия ленты.
     */
    function getSeed() {
        const uid = String((window.APP && window.APP.userId) || 'anon');

        try {
            const cached = sessionStorage.getItem(SEED_KEY);
            // Кэш валиден только для ТОГО ЖЕ юзера: иначе при смене
            // ?user_id= в одной вкладке сид остался бы от предыдущего.
            if (cached && cached.indexOf(uid + '|') === 0) return cached;
        } catch (e) { /* private mode / квота — работаем без кэша */ }

        // Соль устройства: у юзеров без Telegram-initData app-core ставит
        // ОДИН общий fallback-userId, и без соли все они получили бы
        // одинаковую ленту — ровно то, что ротация должна исключать.
        const salt = getDeviceSalt();
        const day = new Date().toISOString().slice(0, 10);
        const seed = `${uid}|${salt}:${day}:v${FEED_VERSION}`;
        try { sessionStorage.setItem(SEED_KEY, seed); } catch (e) { /* ignore */ }
        return seed;
    }

    /** Стабильный случайный идентификатор устройства (переживает сессии). */
    function getDeviceSalt() {
        try {
            let s = localStorage.getItem(SALT_KEY);
            if (!s) {
                s = Math.floor(Math.random() * 0xffffffff).toString(36);
                localStorage.setItem(SALT_KEY, s);
            }
            return s;
        } catch (e) {
            return 'nosalt';
        }
    }

    // ── Anti-repeat store ───────────────────────────────────────────
    // Формат: { "<id>": { n: показов, t: ts последнего показа } }
    // localStorage (не session): анти-повтор должен переживать закрытие
    // Mini App, иначе смысла в нём нет.
    // Кэш стора в памяти: без него каждый импрешен делал полный
    // JSON.parse + JSON.stringify (~64 КБ на потолке 2000 записей)
    // синхронно в main-thread, а импрешены прилетают пачками по ~8 при
    // остановке скролла — это заметный джанк на слабом Android.
    let seenCache = null;
    let seenFlushTimer = null;

    function loadSeen() {
        if (seenCache) return seenCache;
        seenCache = readSeenFromStorage();
        return seenCache;
    }

    function readSeenFromStorage() {
        try {
            const raw = localStorage.getItem(SEEN_KEY);
            if (!raw) return {};
            const data = JSON.parse(raw);
            if (!data || typeof data !== 'object') return {};
            // TTL-чистка на чтении — дешевле, чем отдельный джоб.
            const cutoff = Date.now() - SEEN_TTL_MS;
            let changed = false;
            for (const k in data) {
                if (!data[k] || data[k].t < cutoff) { delete data[k]; changed = true; }
            }
            if (changed) saveSeen(data);
            return data;
        } catch (e) {
            return {};
        }
    }

    function saveSeen(data) {
        try {
            // LRU-cap: при переполнении выкидываем самые старые по t.
            const keys = Object.keys(data);
            if (keys.length > SEEN_MAX_ENTRIES) {
                keys.sort((a, b) => (data[a].t || 0) - (data[b].t || 0));
                const drop = keys.length - SEEN_MAX_ENTRIES;
                for (let i = 0; i < drop; i++) delete data[keys[i]];
            }
            localStorage.setItem(SEEN_KEY, JSON.stringify(data));
        } catch (e) { /* квота исчерпана — анти-повтор просто деградирует */ }
    }

    /**
     * Штраф за «уже видел». Мягкий: не блокируем, а сдвигаем вниз.
     * n показов ЧАС назад — сильный штраф; те же n неделю назад — почти
     * никакого (экспоненциальное затухание с half-life 36ч).
     */
    function seenPenalty(rec, now) {
        if (!rec || !rec.n) return 1;
        const ageH = (now - (rec.t || 0)) / 3600000;
        const decay = Math.pow(0.5, ageH / SEEN_HALF_LIFE_H);
        return 1 / (1 + rec.n * decay);
    }

    /**
     * Отметить карточку показанной. Вызывать по РЕАЛЬНОЙ видимости
     * (IntersectionObserver ≥50%, ≥1с), а не по факту вставки в DOM —
     * иначе быстрый скролл «сожжёт» весь каталог за один свайп.
     */
    function markSeen(id) {
        if (id == null) return;
        const data = loadSeen();           // из памяти, без парса
        const key = String(id);
        const rec = data[key] || { n: 0, t: 0 };
        rec.n = (rec.n || 0) + 1;
        rec.t = Date.now();
        data[key] = rec;
        scheduleFlush();                   // запись — отложенно, пачкой
    }

    /**
     * Откладывает сериализацию в localStorage: импрешены прилетают
     * пачками, писать на каждый — тратить main-thread в момент скролла.
     * Плюс страховка на закрытие/сворачивание Mini App.
     */
    function scheduleFlush() {
        if (seenFlushTimer) return;
        seenFlushTimer = setTimeout(() => {
            seenFlushTimer = null;
            if (seenCache) saveSeen(seenCache);
        }, 2000);
        if (!flushHooksBound) bindFlushHooks();
    }

    let flushHooksBound = false;
    function bindFlushHooks() {
        flushHooksBound = true;
        const flushNow = () => {
            if (seenFlushTimer) { clearTimeout(seenFlushTimer); seenFlushTimer = null; }
            if (seenCache) saveSeen(seenCache);
        };
        // pagehide/visibilitychange — то, что реально срабатывает в
        // мобильном WebView при сворачивании (unload там не гарантирован).
        window.addEventListener('pagehide', flushNow);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') flushNow();
        });
    }

    // ── Полки ───────────────────────────────────────────────────────
    function classify(item, freshCutoffMs) {
        const remix = Number(item.remix_count) || 0;
        // created_at нормализован в normalizeItems (число ms) либо парсим тут.
        const ts = typeof item._createdAtMs === 'number'
            ? item._createdAtMs
            : Date.parse(item.created_at || '') || 0;
        if (ts && ts >= freshCutoffMs) return 'fresh';
        if (remix >= 1) return 'proven';
        return 'explore';
    }

    /**
     * Взвешенное смешивание полок с выполнением квот НА КАЖДОМ окне
     * WINDOW карточек. Дефицит/профицит копится в debt, поэтому квоты
     * соблюдаются и когда полка закончилась.
     */
    function interleave(buckets, rng) {
        const keys = Object.keys(QUOTAS);
        const cursor = {}; const credit = {};
        keys.forEach(k => { cursor[k] = 0; credit[k] = 0; });

        const has = (k) => buckets[k] && cursor[k] < buckets[k].length;
        const total = keys.reduce((n, k) => n + (buckets[k] ? buckets[k].length : 0), 0);
        const out = [];

        // Классический deficit round-robin: каждый раунд все ЖИВЫЕ полки
        // получают кредит, равный своей квоте, берём полку с наибольшим
        // накопленным кредитом и списываем с неё ровно 1 карточку.
        // Частота выбора полки при этом строго пропорциональна квоте.
        //
        // Квоты нормируем по живым полкам, чтобы сумма кредитов за раунд
        // была ровно 1: тогда кредит не уплывает в минус, когда какая-то
        // полка закончилась (у fresh это происходит быстро — их мало),
        // и джиттер сохраняет масштаб до конца ленты.
        while (out.length < total) {
            const active = keys.filter(has);
            if (active.length === 0) break;
            const sumQ = active.reduce((s, k) => s + QUOTAS[k], 0) || 1;
            for (const k of active) credit[k] += QUOTAS[k] / sumQ;

            let best = null; let bestScore = -Infinity;
            for (const k of active) {
                // Джиттер АДДИТИВНЫЙ и мелкий: слегка перемешивает порядок
                // внутри окна, но не ломает пропорции (мультипликативный
                // джиттер на знакопеременном кредите инвертировал бы выбор).
                const score = credit[k] + (rng() - 0.5) * 0.15;
                if (score > bestScore) { bestScore = score; best = k; }
            }
            if (!best) break;
            out.push(buckets[best][cursor[best]++]);
            credit[best] -= 1;
        }
        return out;
    }

    /** created_at карточки в мс. Нормализованное поле кладёт загрузчик. */
    function createdMs(item) {
        if (typeof item._createdAtMs === 'number') return item._createdAtMs;
        return Date.parse(item.created_at || '') || 0;
    }

    /**
     * Поднимает самые новые карточки на позиции PIN_NEW.start и дальше.
     *
     * Порядок остальных не трогаем — только вынимаем поднятые из их
     * прежних мест, иначе они задвоятся. Если новинок меньше count или
     * лента короче start, возвращаем вход как есть: подмес — улучшение,
     * а не обязательное условие ленты.
     */
    function pinNewest(ordered) {
        if (!Array.isArray(ordered) || ordered.length <= PIN_NEW.start) return ordered;

        const cutoff = Date.now() - PIN_NEW.maxAgeDays * 24 * 3600 * 1000;
        const newest = ordered
            .filter(it => createdMs(it) >= cutoff)
            .sort((a, b) => createdMs(b) - createdMs(a))
            .slice(0, PIN_NEW.count);
        if (newest.length === 0) return ordered;

        // Сравниваем по ссылке, а не по id: id бывает строкой и числом,
        // и Set по нему молча пропускал бы дубли.
        const pinned = new Set(newest);
        const rest = ordered.filter(it => !pinned.has(it));
        return rest.slice(0, PIN_NEW.start)
            .concat(newest, rest.slice(PIN_NEW.start));
    }

    function isVideo(item) {
        return item?.output_type === 'video';
    }

    /**
     * Расставляет видео по ленте ступенькой: 3, 7, 11, …
     *
     * Это перестановка, и только: набор карточек тот же, рендер и
     * догрузка ничего об этом не знают. Видео вынимаются из своих мест и
     * вставляются в фиксированные слоты — каждое следующее считается по
     * УЖЕ смещённому массиву, поэтому между соседними видео всегда ровно
     * step − 1 обычных карточек, сколько бы их ни вставилось до этого.
     *
     * Видео больше, чем мест (короткая лента) — остаток просто уезжает в
     * конец: терять карточки нельзя, а растягивать шаг незачем.
     */
    function pinVideos(ordered) {
        if (!Array.isArray(ordered) || ordered.length === 0) return ordered;

        const videos = ordered.filter(isVideo);
        if (videos.length === 0) return ordered;

        const out = ordered.filter((item) => !isVideo(item));
        videos.forEach((video, i) => {
            const at = PIN_VIDEO.first + i * PIN_VIDEO.step;
            if (at >= out.length) out.push(video);
            else out.splice(at, 0, video);
        });
        return out;
    }

    // ── Публичное API ───────────────────────────────────────────────
    /**
     * Основной вход: массив карточек → ротированный порядок.
     * Чистая функция относительно (items, seed, seen): один и тот же
     * ввод даёт один и тот же вывод, поэтому пагинация не ломается.
     *
     * ВАЖНО: вызывать ДО первого рендера батча — приоритеты картинок
     * в карточках позиционные (index<4 → fetchpriority=high), и если
     * переставить порядок после первой отрисовки, LCP-картинки будут
     * не те.
     */
    function rank(items) {
        if (!Array.isArray(items) || items.length === 0) return [];
        const seed = getSeed();
        const rng = makeRng(seed);
        const seen = loadSeen();
        const now = Date.now();
        const freshCutoff = now - FRESH_DAYS * 24 * 3600 * 1000;

        const buckets = { proven: [], fresh: [], explore: [] };

        for (const item of items) {
            const shelf = classify(item, freshCutoff);
            const penalty = seenPenalty(seen[String(item.id)], now);
            // Базовый вес полки:
            //   proven  — по числу ремиксов (log, чтобы топ-1 не подавлял всех)
            //   fresh   — по свежести (новее = выше)
            //   explore — чистый сид-рандом (равные шансы «непрогретым»)
            let base;
            if (shelf === 'proven') {
                base = Math.log1p(Number(item.remix_count) || 0);
            } else if (shelf === 'fresh') {
                const ts = typeof item._createdAtMs === 'number'
                    ? item._createdAtMs : (Date.parse(item.created_at || '') || 0);
                base = 1 + (ts - freshCutoff) / (FRESH_DAYS * 24 * 3600 * 1000);
            } else {
                base = 1;
            }
            // Сид-джиттер: у каждого юзера свой порядок внутри полки.
            const jitter = 0.5 + orderKey(seed, item.id);
            item._rankScore = base * jitter * penalty;
            buckets[shelf].push(item);
        }

        // Внутри полки — по убыванию скора (стабильно: скор детерминирован).
        for (const k in buckets) buckets[k].sort((a, b) => b._rankScore - a._rankScore);

        // Порядок важен: сперва новинки занимают свои позиции, затем по
        // ленте расставляются видео. Наоборот — и подмес новинок сдвинул
        // бы всю видео-ступеньку вниз.
        return pinVideos(pinNewest(interleave(buckets, rng)));
    }

    /** Диагностика (консоль): из чего собралась лента. */
    function debugStats(items) {
        const now = Date.now();
        const freshCutoff = now - FRESH_DAYS * 24 * 3600 * 1000;
        const counts = { proven: 0, fresh: 0, explore: 0 };
        (items || []).forEach(i => { counts[classify(i, freshCutoff)]++; });
        return { seed: getSeed(), counts, seenCount: Object.keys(loadSeen()).length };
    }

    return {
        rank,
        markSeen,
        getSeed,
        debugStats,
        // экспорт для тестов/переиспользования
        _internal: { makeRng, orderKey, seenPenalty, classify, interleave,
                     pinNewest, pinVideos, createdMs, PIN_NEW, PIN_VIDEO },
    };
})();
